"use client"

/**
 * Auth cookies for the admin. Middleware needs to read the token synchronously
 * during request processing — localStorage is not accessible there. These
 * helpers mirror the existing localStorage writes in login/logout so nothing
 * currently working breaks, while enabling the site-gate middleware branch.
 */

import { removeCookie, setCookie } from "@/utils/cookie-helper"

export const ADMIN_AUTH_COOKIE = "admin_auth_token"
export const ADMIN_USER_COOKIE = "admin_user_meta"
// Cờ non-httpOnly để admin-bar phía public biết "phiên này có thể là admin" mà
// không cần đọc token. Chỉ là hint — quyền thật vẫn verify server-side.
export const ADMIN_HINT_COOKIE = "admin_hint"

const AUTH_MAX_AGE_DAYS = 7

const daysFromNow = (days: number) => {
  const d = new Date()
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000)
  return d
}

/** Call right after localStorage.setItem("auth_token", jwt) in the login flow. */
export const writeAuthCookies = (
  jwt: string,
  userMeta: { id: number; username?: string; email?: string; mustChangePassword?: boolean },
): void => {
  if (typeof window === "undefined") return
  const isSecure = window.location.protocol === "https:"
  setCookie(ADMIN_AUTH_COOKIE, jwt, {
    expires: daysFromNow(AUTH_MAX_AGE_DAYS),
    path: "/",
    sameSite: "lax",
    secure: isSecure,
  })
  // Minimal user meta so middleware can branch on sites count / force-change
  // without a fetch. Full user object stays in localStorage as before.
  setCookie(
    ADMIN_USER_COOKIE,
    JSON.stringify({
      id: userMeta.id,
      email: userMeta.email,
      username: userMeta.username,
      mustChangePassword: userMeta.mustChangePassword ?? false,
    }),
    { expires: daysFromNow(AUTH_MAX_AGE_DAYS), path: "/", sameSite: "lax", secure: isSecure },
  )
  // Hint cho admin-bar public (không httpOnly). Giá trị chỉ là cờ "1".
  setCookie(ADMIN_HINT_COOKIE, "1", {
    expires: daysFromNow(AUTH_MAX_AGE_DAYS),
    path: "/",
    sameSite: "lax",
    secure: isSecure,
  })
}

export const clearAuthCookies = (): void => {
  if (typeof window === "undefined") return
  removeCookie(ADMIN_AUTH_COOKIE, { path: "/" })
  removeCookie(ADMIN_USER_COOKIE, { path: "/" })
  removeCookie(ADMIN_HINT_COOKIE, { path: "/" })
}

/**
 * Migrate legacy installations: if localStorage has `auth_token` but the
 * cookie is missing (user was logged in before this feature shipped), mirror
 * the token into the cookie so they don't get logged out on deploy.
 */
export const migrateLegacyAuthCookie = (): void => {
  if (typeof window === "undefined") return
  try {
    const hasCookie = document.cookie.split(";").some(c => c.trim().startsWith(`${ADMIN_AUTH_COOKIE}=`))
    if (hasCookie) return
    const legacyToken = localStorage.getItem("auth_token")
    const legacyUser = localStorage.getItem("user")
    if (!legacyToken || !legacyUser) return
    const parsed = JSON.parse(legacyUser)
    writeAuthCookies(legacyToken, {
      id: parsed?.id,
      email: parsed?.email,
      username: parsed?.username,
    })
  } catch {
    /* ignore */
  }
}
